import { format, isAfter, isBefore, isWithinInterval, parse } from "date-fns";
import {
  FTLBooking,
  FTLCategory,
  FTLCategoryBufferRule,
  FTLCategoryCountRule,
  FTLEventV2,
  FTLTruck,
  FTLTruckBufferRule,
  FTLTruckCountRule,
} from "@/types/index";

type RuleRuling = { description: string; ok: boolean };

type RuleEvaluatorState = {
  categoryCountRules: { [key: string]: FTLCategoryCountRule };
  categoryCountRulesLocationOnly: { [key: string]: FTLCategoryCountRule[] };
  categoryBufferRules: { [key: string]: FTLCategoryBufferRule };
  truckCountRules: { [key: string]: FTLTruckCountRule };
  truckBufferRules: { [key: string]: FTLTruckBufferRule };
  trucks: { [key: string]: FTLTruck };
  categories: { [key: string]: FTLCategory };
};

type BookingContext = {
  booking?: FTLBooking;
  truck?: FTLTruck;
  bookings?: FTLBooking[];
};

export function getCategoryCountRuling(
  state: RuleEvaluatorState,
  context: BookingContext
): RuleRuling {
  const { booking, truck, bookings } = context;
  if (!booking || !truck || !bookings) {
    return { description: "", ok: true };
  }

  const rules = state.categoryCountRulesLocationOnly[`${booking.event}`];
  if (!rules) {
    return { description: "", ok: true };
  }

  const categoryName = state.categories[truck.category]?.title;
  if (!categoryName) {
    return {
      description: "Truck hasn't been assigned a category",
      ok: false,
    };
  }

  const matchesCategory = rules.some((rule) =>
    rule.categories.includes(truck.category)
  );

  if (!matchesCategory) {
    return { description: `${categoryName} trucks not allowed`, ok: false };
  }

  const rule = state.categoryCountRules[`${booking.event}-${truck.category}`];
  const total = rule.count;
  const booked = booking.scheduledTrucks
    .filter((t) => !!state.trucks[t]?.category)
    .filter((t) => state.trucks[t].category === truck.category).length;
  return {
    description: `${booked} booking(s) out of ${total} for ${categoryName} trucks`,
    ok: total > booked,
  };
}

export function getCategoryBufferRuling(
  state: RuleEvaluatorState,
  context: BookingContext
): RuleRuling {
  const { booking, truck, bookings } = context;
  if (!booking || !truck || !bookings) {
    return { description: "", ok: true };
  }
  const rule = state.categoryBufferRules[`${booking.event}-${truck.category}`];
  if (!rule) {
    return { description: "", ok: true };
  }

  const locationEvents = bookings.filter((b) => b.event === booking.event);

  const indexOfEvent = locationEvents.findIndex((e) => e.id === booking.id);
  const pastEvents = locationEvents.slice(0, indexOfEvent);
  let pastBufferOk = true;
  for (let i = pastEvents.length - 1; i >= pastEvents.length - rule.buffer; i--) {
    if (
      pastEvents[i]?.scheduledTrucks?.some(
        (t) => state.trucks[t]?.category === truck.category
      )
    ) {
      pastBufferOk = false;
      break;
    }
  }

  const futureEvents = locationEvents.slice(indexOfEvent + 1);
  let futureBufferOk = true;
  for (let i = 0; i < rule.buffer; i++) {
    if (
      futureEvents[i]?.scheduledTrucks?.some(
        (t) => state.trucks[t]?.category === truck.category
      )
    ) {
      futureBufferOk = false;
      break;
    }
  }

  const categoryName = state.categories[truck.category].title;

  return {
    description: `${categoryName} bookings must be more than ${rule.buffer} event(s) apart`,
    ok: pastBufferOk && futureBufferOk,
  };
}

export function getTruckCountRuling(
  state: RuleEvaluatorState,
  context: BookingContext
): RuleRuling {
  const { booking, truck, bookings } = context;
  if (!booking || !truck || !bookings) {
    return { description: "", ok: true };
  }
  const rule = state.truckCountRules[`${booking.event}-${truck.id}`];
  if (!rule) {
    return {
      description: `${truck.title} bookings not currently available for ${booking.title}`,
      ok: false,
    };
  }

  const eventDate = parse(
    booking.date.split("T")[0],
    "yyyy-MM-dd",
    new Date()
  );
  const ruleStartDate = rule.startDate
    ? parse(rule.startDate.split("T")[0], "yyyy-MM-dd", new Date())
    : null;
  const ruleEndDate = rule.endDate
    ? parse(rule.endDate.split("T")[0], "yyyy-MM-dd", new Date())
    : null;
  if (ruleStartDate && ruleEndDate) {
    if (isBefore(eventDate, ruleStartDate) || isAfter(eventDate, ruleEndDate)) {
      return {
        description: `${truck.title} bookings only allowed between ${format(
          ruleStartDate,
          "MMM do yyyy"
        )} - ${format(ruleEndDate, "MMM do yyyy")}`,
        ok: false,
      };
    }
  }

  const total = rule.count;
  const booked = bookings
    .filter((b) => b.event === booking.event)
    .filter((b) => {
      if (!ruleStartDate || !ruleEndDate) {
        return true;
      }
      const eDate = parse(b.date.split("T")[0], "yyyy-MM-dd", new Date());
      return isWithinInterval(eDate, {
        start: ruleStartDate,
        end: ruleEndDate,
      });
    })
    .filter((b) => b.scheduledTrucks.some((t) => t === truck.id)).length;

  if (ruleStartDate && ruleEndDate) {
    return {
      description: `${booked} booking(s) out of ${total} for ${
        truck.title
      } for ${format(ruleStartDate, "MMM do yyyy")} - ${format(
        ruleEndDate,
        "MMM do yyyy"
      )}`,
      ok: total > booked,
    };
  }

  return {
    description: `${booking.title} events not available for ${truck.title}`,
    ok: false,
  };
}

export function getTruckBufferRuling(
  state: RuleEvaluatorState,
  context: BookingContext
): RuleRuling {
  const { booking, truck, bookings } = context;
  if (!booking || !truck || !bookings) {
    return { description: "", ok: true };
  }
  const rule = state.truckBufferRules[`${booking.event}-${truck.id}`];
  if (!rule) {
    return { description: "", ok: true };
  }

  const locationEvents = bookings.filter((b) => b.event === booking.event);

  const indexOfEvent = locationEvents.findIndex((b) => b.id === booking.id);
  const pastBookings = locationEvents.slice(0, indexOfEvent);
  let pastBufferOk = true;
  for (
    let i = pastBookings.length - 1;
    i >= pastBookings.length - rule.buffer;
    i--
  ) {
    if (pastBookings[i]?.scheduledTrucks?.some((t) => t === truck.id)) {
      pastBufferOk = false;
      break;
    }
  }

  const futureBookings = locationEvents.slice(indexOfEvent + 1);
  let futureBufferOk = true;
  for (let i = 0; i < rule.buffer; i++) {
    if (futureBookings[i]?.scheduledTrucks?.some((t) => t === truck.id)) {
      futureBufferOk = false;
      break;
    }
  }

  return {
    description: `${truck.title} bookings must be more than ${rule.buffer} event(s) apart`,
    ok: pastBufferOk && futureBufferOk,
  };
}

export function getLocationCountRuling(
  booking?: FTLBooking,
  event?: FTLEventV2
): RuleRuling {
  if (!booking || !event) {
    return { description: "", ok: true };
  }
  const bookedTruckCount = booking.scheduledTrucks.length;
  const total =
    booking.maxTrucks !== null
      ? booking.maxTrucks
      : event.maxTrucks !== null
      ? event.maxTrucks
      : 0;
  let ok = true;
  if (bookedTruckCount >= total) {
    ok = false;
  }
  return {
    description: `${bookedTruckCount} truck(s) out of ${total} for ${booking.title}`,
    ok,
  };
}
